import { Link } from "@tanstack/react-router";
import { ArrowUpRight, type LucideIcon } from "lucide-react";
import { useRef, type MouseEvent } from "react";

export function IndustryCard({
  slug,
  title,
  image,
  index,
  Icon,
  titleAs = "h3",
}: {
  slug: string;
  title: string;
  image: string;
  index: number;
  Icon: LucideIcon;
  titleAs?: "h2" | "h3";
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  const frame = useRef(0);

  const onMove = (e: MouseEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    if (!el) return;
    if (frame.current) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    frame.current = requestAnimationFrame(() => {
      const px = (x / rect.width - 0.5) * 2; // -1..1
      const py = (y / rect.height - 0.5) * 2;
      el.style.setProperty("--mx", `${(x / rect.width) * 100}%`);
      el.style.setProperty("--my", `${(y / rect.height) * 100}%`);
      el.style.setProperty("--tx", `${(-px * 10).toFixed(2)}px`);
      el.style.setProperty("--ty", `${(-py * 10).toFixed(2)}px`);
      frame.current = 0;
    });
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--tx", `0px`);
    el.style.setProperty("--ty", `0px`);
    el.style.setProperty("--mx", `50%`);
    el.style.setProperty("--my", `50%`);
  };

  const TitleTag = titleAs;

  return (
    <Link
      ref={ref}
      to="/industries/$slug"
      params={{ slug }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        // initial vars
        ["--mx" as string]: "50%",
        ["--my" as string]: "50%",
        ["--tx" as string]: "0px",
        ["--ty" as string]: "0px",
      }}
      className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border border-border-strong/40 bg-background shadow-card hover:shadow-elevated transition-shadow duration-700 [transform:translateZ(0)]"
    >
      {/* Parallax image */}
      <div
        className="absolute inset-[-6%] will-change-transform transition-transform duration-[400ms] ease-out"
        style={{ transform: "translate3d(var(--tx), var(--ty), 0)" }}
      >
        <img
          src={image}
          alt={`${title} — premium environment`}
          loading="lazy"
          width={1280}
          height={1600}
          className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
        />
      </div>

      {/* Base gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-700" />

      {/* Light sweep — radial follow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-soft-light"
        style={{
          background:
            "radial-gradient(420px circle at var(--mx) var(--my), rgba(255,255,255,0.55), rgba(255,255,255,0) 60%)",
        }}
      />

      {/* Top row: index + line icon glass chip */}
      <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
        <span className="font-mono text-[10px] text-white/80 tracking-widest glass-dark px-2.5 py-1 rounded-full">
          0{index + 1}
        </span>
        <div className="glass-dark inline-flex h-11 w-11 items-center justify-center rounded-xl text-white">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.4} />
        </div>
      </div>

      {/* Bottom caption */}
      <div className="absolute inset-x-0 bottom-0 p-7">
        <div className="hairline mb-5 opacity-40" />
        <TitleTag className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight">
          {title}
        </TitleTag>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
          Explore Sector
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}
